import {Button} from "@heroui/react"
import {OptionMap, OptionTypes} from "./utils"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
} from "@heroui/drawer"
import FiltersList from "./FiltersList"
import React from "react"
import SelectedFiltersListView from "./SelectedFiltersListView"

const FiltersDrawerMenu = ({
  isOpen,
  onOpenChange,
  submitFilters,
  currentFilters = [],
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  submitFilters: (filters: OptionMap[]) => void
  currentFilters?: OptionMap[]
}) => {
  const [options, setOptions] = React.useState<OptionMap[]>([])

  /**
   * Initialize options from currentFilters whenever drawer is opened
   * Ensures drawer state is fresh each time the drawer opens
   */
  React.useEffect(() => {
    if (isOpen) {
      // Shallow copy the current filters to use in the drawer
      if (currentFilters && currentFilters.length > 0) {
        setOptions([...currentFilters])
      } else {
        // Reset options state when drawer opens with no current filters
        setOptions([])
      }
    }
  }, [isOpen, currentFilters])

  function addOption(optionType: OptionTypes) {
    if (optionType === "date_range") {
      setOptions((prev) => {
        const newMap = [...prev]
        newMap.push({
          type: "date_range",
          temp_id: crypto.randomUUID(),
          value: {start: "", end: ""},
        })
        return newMap
      })
    }
    if (optionType === "contributors") {
      setOptions((prev) => {
        const newMap = [...prev]
        newMap.push({
          type: "contributors",
          temp_id: crypto.randomUUID(),
          value: {contributors: []},
        })
        return newMap
      })
    }
  }

  function removeOption(option: OptionMap) {
    setOptions((prev) => {
      return prev.filter((opt) => opt.temp_id !== option.temp_id)
    })
  }

  function changeValue(
    option: OptionMap,
    type: OptionTypes,
    value: OptionMap["value"]
  ) {
    // update the value of the option
    if (type === "contributors") {
      setOptions((prev) => {
        return prev.map((opt) => {
          if (opt.temp_id === option.temp_id) {
            return {...opt, value: value}
          }
          return opt
        })
      })
    }

    if (type === "date_range") {
      setOptions((prev) => {
        return prev.map((opt) => {
          if (opt.temp_id === option.temp_id) {
            return {...opt, value: value}
          }
          return opt
        })
      })
    }
  }

  /**
   * Filters options to only return valid ones with complete data
   * Ensures no empty/incomplete filters are submitted
   */
  const getValidFilters = () => {
    return options.filter((option) => {
      if (option.type === "date_range") {
        // Date ranges need both start and end dates
        return option.value.start && option.value.end
      }
      if (option.type === "contributors") {
        // Contributors filter must have at least one valid contributor
        return (
          Array.isArray(option.value.contributors) &&
          option.value.contributors.length > 0
        )
      }
      return false
    })
  }

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          // Submit filters when drawer is closing
          submitFilters(getValidFilters())
        }
        onOpenChange(open)
      }}
    >
      <DrawerContent>
        {(onClose) => (
          <>
            <DrawerHeader className="flex flex-col gap-1">
              Filters and Sorting
            </DrawerHeader>
            <DrawerBody>
              <FiltersList onAdd={addOption} options={options} />
              <SelectedFiltersListView
                options={options}
                removeOption={removeOption}
                changeValue={changeValue}
              />
            </DrawerBody>
            <DrawerFooter>
              <Button color="danger" variant="light" onPress={onClose}>
                Close
              </Button>
              <Button color="primary" onPress={onClose}>
                Add Filters
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  )
}

export default FiltersDrawerMenu
